import { AccountMediaImage } from "./AccountMedia";
import { readStoryCalendar, formatStoryClock } from '@private-fiction/story-core';
import type { SceneHeaderTemplate, WorldDocument } from "@private-fiction/story-core";

interface SceneHeaderProps {
  image?: string;
  location?: (WorldDocument) | undefined;
  clock?: (WorldDocument) | undefined;
  ready: boolean;
  template?: (SceneHeaderTemplate) | undefined;
}

export function SceneHeader({ image, location, clock, ready, template }: SceneHeaderProps) {
  if (!ready) return <div className="scene-header-skeleton" aria-label="กำลังเตรียมข้อมูลฉาก" />;
  if (!location && !clock && !image) return null;
  const fields = { ...clock?.fields, ...(clock?.runtimeState ?? {}) };
  const reading = readStoryCalendar(fields);
  const formatted = reading ? formatStoryClock(reading.calendar,reading.state) : null;
  const parsedTime = typeof fields.currentTime === "string" ? new Date(fields.currentTime) : null;
  const validTime = parsedTime && Number.isFinite(parsedTime.getTime()) ? parsedTime : null;
  const clockTime = formatted ? formatted.time : validTime
    ? validTime.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })
    : [fields.time, fields.period].filter((value) => value != null && String(value) !== "").map(String).join(" · ");
  const clockDate = formatted ? formatted.date : validTime
    ? validTime.toLocaleDateString("th-TH", { day: "numeric", month: "long", year: "numeric" })
    : [fields.calendar, fields.day, fields.year == null ? "" : `Year ${String(fields.year)}`].filter((value) => value != null && String(value) !== "").map(String).join(" ");
  const weather = String(fields.weather ?? location?.fields.weather ?? "");
  const available = {
    "worldClock.time": clockTime,
    "worldClock.date": clockDate,
    "worldClock.weather": weather,
    "worldClock.season": formatted?.season ?? '',
    "scene.activePressure": String(location?.fields.danger ?? ""),
    "character.conditions.0": "",
  };
  const slots = template?.slots
    .filter((slot) => available[slot.sourceBinding as keyof typeof available])
    .sort((left, right) => right.priority - left.priority)
    .slice(0, template.maxVisibleSlots)
    .map((slot) => ({ id: slot.id, value: available[slot.sourceBinding as keyof typeof available], label: slot.label, emoji: slot.emoji }))
    ?? [
      { id: "time", value: clockTime, label: "เวลา", emoji: "◷" },
      { id: "date", value: clockDate, label: "วันที่", emoji: "◈" },
      { id: "pressure", value: String(location?.fields.danger ?? ""), label: "แรงกดดัน", emoji: "⌁" },
    ].filter((slot) => slot.value);
  const hasImage = Boolean(image);
  return (
    <section
      className={`scene-header scene-header--${template?.layout ?? "cinematic"} ${hasImage ? "scene-header--has-image" : "scene-header--no-image"} enter`}
      aria-label="ข้อมูลฉากปัจจุบัน"
    >
      {image && (
        <div className="scene-header__backdrop" aria-hidden="true">
          <AccountMediaImage source={image} alt="" className="scene-header-image" />
          <div className="scene-header__overlay" />
        </div>
      )}
      <div className="scene-header__body">
        <div className="scene-header__eyebrow"><span>LIVE SCENE</span><i />ข้อมูลของเทิร์น</div>
        <div className="scene-header__main">
          <div>
            <p>ตำแหน่งปัจจุบัน</p>
            <h1>{location?.title || "ฉากปัจจุบัน"}</h1>
          </div>
          {!template && weather && <div className="scene-header__weather" aria-label="สภาพอากาศ">☂ {weather}</div>}
        </div>
        <div className="scene-header__meta">
          {slots.map((slot) => <span key={slot.id} title={slot.label}>{slot.emoji} {slot.value}</span>)}
        </div>
      </div>
    </section>
  );
}
